import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Tabs, Tab, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import { getLocationForBusinessDataApi } from 'SupabseHandler';
import { SelectedPatient, businessData as businessDataAtom, errorsState } from 'atoms';
import Button from 'common/Button';
import FormModal from 'common/FormModal';
import Heading from 'common/Heading';
import PageStructure from 'common/PageStructure';
import SubHeading from 'common/SubHeading';
import { timeSlots } from 'common/constant';
import AccountCreated from 'emails/AccountCreated';
import NewPatientAccount from 'emails/NewPatient';
import PatientAction from 'emails/PatientActionRequired';
import PatientAppointment from 'emails/PatientAppointment';
import { materialTheme } from 'materialTheme';
import moment from 'moment';
import AppointmentPopup from 'pages/Appointment/Partials/AppointmentPopup';
import DayView from 'pages/Appointment/Partials/DayView';
import FilterButton from 'pages/Appointment/Partials/FilterButton';
import MonthView from 'pages/Appointment/Partials/MonthView';
import Navigation from 'pages/Appointment/Partials/Navigation';
import WeakView from 'pages/Appointment/Partials/WeakView';
import ReactDOMServer from 'react-dom/server';
import { toast } from 'react-toastify';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';
import { theme } from 'theme';
import { sendEmail } from 'utils/methods';
import { toasterNotification } from 'utils/toaster';
import { useAuth } from 'context/AuthContext';

import React, { useCallback, useEffect, useRef, useState } from 'react';

// Helper function to remove commas from price strings before parsing
const cleanPrice = (price) => {
    if (typeof price === 'string') {
        return price.replace(/,/g, '');
    }
    return price;
};

const Appointment = () => {
    const { token, loading: authLoading } = useAuth();
    const shouldScroll = useMediaQuery('(max-width: 975px)');
    const isTabletOrBelow = useMediaQuery('(max-width: 960px)');
    const isMobile = useMediaQuery('(max-width: 640px)');
    const isCompactHeader = useMediaQuery('(max-width: 1100px)'); // Change layout when content would overflow on one row
    const [validationErrors, setValidationErrors] = useRecoilState(errorsState);
    const [selectedPatient, setSelectedPatient] = useRecoilState(SelectedPatient);
    const [providers, setProviders] = useState([]);
    const [providerFullDetails, setProviderFullDetails] = useState([]);
    const providersRef = useRef([]);
    useEffect(() => { providersRef.current = providers; }, [providers]);

    const [value, setValue] = useState('3');
    const [openPopup, setOpenPopup] = useState(false);
    const [formData, setFormData] = useState({
        form_status: 'add',
    });
    const [appointments, setAppointments] = useState([]);
    const [processedAppointments, setProcessedAppointments] = useState([]);
    const [pageTabHeading, setPageTabHeading] = useState();

    // Helper function to format date heading - only show year if different from current year
    const formatDateHeading = (date, viewType) => {
        const currentYear = new Date().getFullYear();
        const dateYear = date.getFullYear();
        const showYear = dateYear !== currentYear;
        
        const month = date.toLocaleString('default', { month: 'long' });
        
        if (viewType === 'month') {
            // Month view: "November" or "November 2024"
            return showYear ? `${month} ${dateYear}` : month;
        } else if (viewType === 'week') {
            // Week view: "November 20-26" or "November 20-26, 2024"
            const startDate = date.getDate();
            const endDate = new Date(moment(date).add(6, 'days')).getDate();
            return showYear 
                ? `${month} ${startDate}-${endDate}, ${dateYear}` 
                : `${month} ${startDate}-${endDate}`;
        } else if (viewType === 'day') {
            // Day view: "November 20" or "November 20, 2024"
            const day = date.getDate();
            return showYear ? `${month} ${day}, ${dateYear}` : `${month} ${day}`;
        }
    };
    const [weakStartDate, setWeakStartDate] = useState();
    const [weakEndDate, setWeakEndDate] = useState();
    const [appointmentId, setAppointmentId] = useState(null);

    const [selectedMonth, setSelectedMonth] = useState();
    const [monthStartDate, setMonthStartDate] = useState();

    const [selectedDate, setSelectedDate] = useState();

    const [business, setBusiness] = useRecoilState(businessDataAtom);
    const [loading, setLoading] = useState(false);

    const getProviders = useCallback(async () => {
        setLoading(true);
        try {
            if (!business?.id || !token) {
                setProviders([]);
                setProviderFullDetails([]);
                return;
            }

            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/provider-full-details`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ business_id: business.id }),
                }
            );

            const result = await response.json();

            if (!response.ok || result.error) {
                setProviders([]);
                setProviderFullDetails([]);
                return;
            }

            const fullItems = Array.isArray(result.data) ? result.data : (result.data ? [result.data] : []);
            // Store full items for downstream reuse (ooo, availability, services)
            setProviderFullDetails(fullItems);
            // Expose flat provider objects to AppointmentPopup / FilterButton
            setProviders(fullItems.map((item) => item.provider ?? item));
        } catch (error) {
            console.error('Error fetching providers:', error);
            setProviders([]);
            setProviderFullDetails([]);
        } finally {
            setLoading(false);
        }
    }, [token, business?.id]);

    const checkProviderOutOfOffice = (dateOfService, timeOfService, oooList) => {
        const serviceDateTime = new Date(`${dateOfService}T${timeOfService}Z`); // 'Z' indicates UTC time
        return oooList.some((ooo) => {
            const oooStart = new Date(ooo.date_start);
            const oooEnd = new Date(ooo.date_end);
            return serviceDateTime >= oooStart && serviceDateTime <= oooEnd;
        });
    };

    const checkProviderAvailability = (dateOfService, timeOfService, providerAvailability, duration) => {
        const selectedDate = new Date(dateOfService);
        const selectedDay = selectedDate.getDay();

        const availability = providerAvailability.find((slot) => Number(slot.day) === selectedDay);

        if (!availability) {
            return false;
        }        const timeToMinutes = (time) => {
            const [hours, minutes] = time.split(':').map(Number);
            return hours * 60 + minutes;
        };        // Convert the time slots into minutes
        const timeSlotMinutes = timeSlots.map((slot) => ({
            ...slot,
            timeInMinutes: timeToMinutes(slot.time),
        }));
        const selectedTime = timeToMinutes(timeOfService);
        const startTime = timeSlotMinutes.find(
            (slot) => slot.id === Number(availability.start_datetime),
        )?.timeInMinutes;
        const endTime = timeSlotMinutes.find((slot) => slot.id === Number(availability.end_datetime))?.timeInMinutes;
        return selectedTime >= startTime && selectedTime <= endTime;
    };

    const handleTabChange = (event, newValue) => {
        if (newValue === '1') {
            const date = new Date();
            const firstOfCurrentMonth = new Date(date.getFullYear(), date.getMonth(), 1);
            setSelectedMonth(date);
            setMonthStartDate(firstOfCurrentMonth);
            setPageTabHeading(formatDateHeading(date, 'month'));
        }

        if (newValue === '2') {
            let currentDay = new Date().getDay();
            let dateOfDayZero = new Date(new Date().setDate(new Date().getDate() - currentDay + 1));

            setWeakStartDate(dateOfDayZero);
            setWeakEndDate(new Date(moment(dateOfDayZero).add(6, 'days')));

            setPageTabHeading(formatDateHeading(dateOfDayZero, 'week'));
        }

        if (newValue === '3') {
            const date = new Date();
            setSelectedDate(date);
            setPageTabHeading(formatDateHeading(date, 'day'));
        }

        setValue(newValue);
    };

    const handleEditAppointment = (e, appointment) => {
        // Check if appointment has either a patient_id or new_patient data
        if (!appointment?.patient_id && !appointment?.new_patient) {
            return;
        } else {
            const currentDate = new Date().setHours(0, 0, 0, 0);
            const appointmentDate = new Date(appointment?.appointment_date).setHours(0, 0, 0, 0);
            
            setAppointmentId(appointment?.id);
            
            // Use enhanced service_data from the processed appointment if available
            const enhancedServiceData = appointment?.service_data || appointment?.service_data || [];
            
            // Determine if this is a past appointment
            const isPastAppointment = appointmentDate < currentDate;
            
            // Handle patient data - either from linked patient or new_patient field
            let patientData = {};
            let patientName = '';
            
            if (appointment?.patient_id && appointment?.patient) {
                // Existing patient
                patientData = {
                    patient: appointment?.patient_id,
                };
                patientName = `${appointment?.patient?.first_name} ${appointment?.patient?.last_name}`;
            } else if (appointment?.new_patient) {
                // New patient data stored in appointment
                patientData = {
                    name: appointment?.new_patient?.full_name,
                    email: appointment?.new_patient?.email,
                    phone: appointment?.new_patient?.contact_number,
                    dob: appointment?.new_patient?.dob,
                    gender: appointment?.new_patient?.gender,
                };
                patientName = appointment?.new_patient?.full_name || `${appointment?.new_patient?.first_name} ${appointment?.new_patient?.last_name}`;
            }
            
            const isBusinessAppointment = !appointment?.provider_id;
            setFormData({
                ...formData,
                ...patientData,
                providerDetail: appointment?.provider,
                appointment_type: appointment?.appointment_type,
                provider: isBusinessAppointment ? `business_${business?.id}` : appointment?.provider_id,
                dateOfService: appointment?.appointment_date,
                timeOfService: appointment?.time,
                servicesData: enhancedServiceData,
                duration: appointment?.service_duration,
                paymentStatus: appointment?.payment_status,
                appointmentStatus: isPastAppointment && !(appointment?.status || '')?.toLowerCase()?.includes('cancelled')
                    ? 'Past'
                    : appointment?.status,
                form_status: isPastAppointment ? 'view' : 'edit',
                id: appointment?.id,
                location_id: appointment?.location_id ?? formData?.location_id,
                patient_name: patientName,
                isPastAppointment: isPastAppointment,
                new_patient: appointment?.new_patient || null, // Include new_patient data for resend email functionality
            });
            setOpenPopup(true);
        }
    };

    const handlePopupToggle = () => {
        if (providers?.length === 0) {
            toasterNotification('info', '', 'Please add a provider to proceed.');
            return;
        } else {
            setOpenPopup(!openPopup);
            setFormData({ form_status: 'add' });
            setValidationErrors({});
        }
    };

    const sendEmailToPatient = async(
        patientEmail,
        appointmentId,
        firstName,
        businessName,
        buisnessEmail,
        time,
        date,
        providerName,
        visitCost,
        location,
        servicesName,
    ) => {
        if (!token) {
            console.error('No authentication token found');
            return;
        }
        const emailContent = `
        <!DOCTYPE html>
        ${ReactDOMServer.renderToString(
            <PatientAction
                patientName={firstName}
                businessName={businessName}
                buisnessEmail={buisnessEmail} //required phone number here
                appointmentTime={time}
                appointmentDate={date}
                providerName={providerName}
                visitCost={visitCost}
                location={location}
                serviceNames={servicesName}
                appointmentId={appointmentId}
            />,
        )}
    `;
        sendEmail(token,patientEmail, 'Subject: Action Required: Review and Confirm Your Appointment', ' ', emailContent, '', false);
    };

    const sendEmailToNewPatient = async (
        patientName,
        patientEmail,
        businessName,
        buisnessEmail, //required phone number here
        dob,
        gender,
        phoneNumber,
        appointmentId,
    ) => {
        if (!token) {
            console.error('No authentication token found');
            return;
        }
        const emailContent = `
        <!DOCTYPE html>
        ${ReactDOMServer.renderToString(
            <NewPatientAccount
                patientName={patientName}
                patientEmail={patientEmail}
                businessName={businessName}
                buisnessEmail={buisnessEmail}
                dob={dob}
                gender={gender}
                phoneNumber={phoneNumber} //required phone number here
                appointmentId={appointmentId}
            />,
        )}
    `;
        sendEmail(token,patientEmail, 'Subject: Action Required: Sign up to verify your appointment.', ' ', emailContent, '', false);
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            //new patient validations
            const { name, email, gender, contact_number,phone, dob } = formData;
            
            if (name && email && gender && phone && dob) {
                let isInvalid;
                const namePattern = /^[A-Za-z]+( [A-Za-z]+)+$/;
                const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                let errors = {};

                if (!namePattern.test(name)) {
                    errors.name = "Name must be in 'First Last' format.";
                    isInvalid = true;
                }
                const rawPhone = String(phone).replace(/\D/g, "");
                // Accept 10 digits (US) or 11 digits starting with country code '1'
                const isValidPhone = rawPhone.length === 10 || (rawPhone.length === 11 && rawPhone.startsWith('1'));
                if (!isValidPhone) {
                    errors.phone = 'Invalid Phone Number.';
                    isInvalid = true;
                }

                if (!emailPattern.test(email)) {
                    errors.email = 'Invalid email.';
                    isInvalid = true;
                }

                const birthDate = new Date(dob);
                const today = new Date();
                let age = today.getFullYear() - birthDate.getFullYear();
                const monthDiff = today.getMonth() - birthDate.getMonth();
                const dayDiff = today.getDate() - birthDate.getDate();

                if (age < 18 || (age === 18 && (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)))) {
                    toasterNotification('error','Patient must be atleast 18 years old.')
                    isInvalid = true;
                }

                setValidationErrors(errors);
                if (isInvalid) {
                    return;
                }
            }

            const { provider, dateOfService, timeOfService } = formData;

            const isBusinessSelection = typeof formData?.provider === 'string' && String(formData?.provider).startsWith('business_');
            const numericProviderId = isBusinessSelection ? null : Number(formData?.provider);

            let selectedProviderOooList = [];
            let providerAvailablity = [];
            let providerDetails = [];
            if (!isBusinessSelection && numericProviderId) {
                selectedProviderOooList = await getOooByProviderId(numericProviderId);
                const fetched = await getProviderAvailablity(numericProviderId);
                providerAvailablity = fetched?.providerAvailablity || [];
                providerDetails = fetched?.providerDetails || [];
            }

            //Is provider out of office or not?
            if (selectedProviderOooList?.length > 0) {
                const isUnavailable = checkProviderOutOfOffice(dateOfService, timeOfService, selectedProviderOooList);
                if (isUnavailable) {
                    toasterNotification('info', '', 'Provider is out of office during the selected date and time');
                    return;
                }
            }
            // //Provider availablility match with the appointement time and date or not?
            // const isProviderAvailable = checkProviderAvailability(
            //     dateOfService,
            //     timeOfService,
            //     providerAvailablity,
            //     formData?.duration,
            // );
            // if (!isProviderAvailable) {
            //     toasterNotification('info', '', 'Provider is not available during the selected date and time. ');
            //     return;
            // }

            // const isProviderBooked = appointments.some(
            //     (appointment) =>
            //         Number(appointment?.provider_id) === Number(provider) &&
            //         appointment?.appointment_date === dateOfService &&
            //         appointment?.time === timeOfService &&
            //         appointment?.id !== formData?.id,
            // );

            const serviceIds = formData?.servicesData?.map((item) => Number(item.service)).filter(id => id); // Filter out null/undefined/0 values
            const uniqueIds = new Set(serviceIds);            // if (isProviderBooked) {
            //     toasterNotification(
            //         'info',
            //         '',
            //         'The selected time for this provider is already booked. Please choose a different time.',
            //     );
            // } else 
            if (serviceIds && serviceIds?.length !== uniqueIds.size) {
                toasterNotification('info', '', 'Services must be different to proceed');
            } else {
                
                if (appointmentId && formData?.form_status !== 'add') {
                    const isTelemedicine = formData?.location_id === 'telemedicine' || formData?.appointment_type === 'telemedicine';
                    const persistedLocationId = isTelemedicine ? null : (formData?.location_id ? Number(formData.location_id) : null);
                    // Get duration - use formData.duration if set, otherwise fall back to provider's default
                    const duration = isBusinessSelection
                        ? 15
                        : (
                            formData?.duration || (
                                isTelemedicine
                                    ? (formData?.providerDetail?.telemedicine_consultation_duration || 30)
                                    : (formData?.providerDetail?.in_person_consultation_duration || 30)
                            )
                        );
                    // Validate access token
                    if (!token) {
                        console.error('No authentication token found');
                        toasterNotification('error', '', 'Authentication required. Please log in again.');
                        return;
                    }

                    // Prepare update payload
                    const updatePayload = {
                        provider_id: numericProviderId,
                        patient_id: formData.patient,
                        appointment_date: formData.dateOfService,
                        time: formData.timeOfService,
                        service_data: formData?.servicesData?.map(service => ({
                            ...service,
                            service: service.service || null, // Handle empty service selection
                            serviceName: service.serviceName || '', // Handle empty service name
                            price: parseFloat(cleanPrice(service.price))
                        })) ?? [],
                        service_duration: duration,
                        location_id: persistedLocationId,
                        appointment_type: isTelemedicine ? 'telemedicine' : 'in_office',
                        status: formData.appointmentStatus,
                    };

                    // Call backend API to update appointment
                    const response = await fetch(
                        `${process.env.REACT_APP_SERVER_URL}/api/providers/appointments`,
                        {
                            method: 'PUT',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({ id: appointmentId, ...updatePayload })
                        }
                    );

                    const result = await response.json();

                    if (!response.ok || result.error) {
                        const errorMessage = result.error || 'Failed to update appointment';
                        console.error('Error updating appointment:', errorMessage);
                        toasterNotification('error', '', errorMessage);
                        return;
                    }

                    handlePopupToggle();
                    getAppointmentList().then(() => {
                        setFormData({
                            form_status: 'add',
                        });
                    });
                    // Show "Appointment updated" message
                    toasterNotification('success', 'Appointment updated');
                    return;
                }
                const mainLocation = await getBusinessMainLocation();
                const isTelemedicine = formData?.location_id === 'telemedicine' || formData?.appointment_type === 'telemedicine';
                const persistedLocationId = isTelemedicine ? null : (formData?.location_id ? Number(formData.location_id) : null);
                
                // Get location for email - use telemedicine URL if telemedicine appointment
                let emailLocation = mainLocation;
                if (!isBusinessSelection && isTelemedicine && providerDetails) {
                    const providerDetail = Array.isArray(providerDetails) ? providerDetails[0] : providerDetails;
                    if (providerDetail?.telemedicine_url) {
                        emailLocation = providerDetail.telemedicine_url;
                    }
                }
                
                const patientEmail = formData?.email ? formData?.email : selectedPatient?.email;
                // Debug logs for servicesData
                // Defensive reduce for visitCost
                const visitCost = Array.isArray(formData?.servicesData)
                    ? formData.servicesData.reduce((sum, item) => {
                        const price = item && item.price !== undefined && item.price !== null ? cleanPrice(item.price) : 0;
                        return sum + Number(price || 0);
                    }, 0)
                    : 0;
                // Defensive map for serviceNames
                const serviceNames = Array.isArray(formData?.servicesData)
                    ? formData.servicesData.map((item, idx) => {
                        if (!item) {
                            return '';
                        }
                        if (typeof item.serviceName === 'undefined') {
                        }
                        return item.serviceName || '';
                    }).join(', ')
                    : '';

                // Prepare new patient data if it's a new patient
                let newPatientData = null;
                if (formData?.name && formData?.email && formData?.gender && formData?.phone && formData?.dob) {
                    const [firstName, ...lastNameParts] = formData.name.split(' ');
                    const lastName = lastNameParts.join(' ');
                    const rawPhone = String(formData.phone).replace(/\D/g, "");
                    
                    newPatientData = {
                        first_name: firstName,
                        last_name: lastName,
                        email: formData.email,
                        contact_number: rawPhone,
                        dob: formData.dob,
                        gender: formData.gender,
                        full_name: formData.name
                    };
                }

                // Get duration - use formData.duration if set, otherwise fall back to provider's default
                const duration = isBusinessSelection
                    ? 15
                    : (
                        formData?.duration || (
                            isTelemedicine
                                ? (formData?.providerDetail?.telemedicine_consultation_duration || 30)
                                : (formData?.providerDetail?.in_person_consultation_duration || 30)
                        )
                    );

                // Create appointment via backend API instead of direct Supabase call
                if (!token) {
                    throw new Error('No authentication token found');
                }

                const createPayload = {
                    business_id: business.id,
                    provider_id: numericProviderId,
                    patient_id: formData.patient || null,
                    appointment_date: formData.dateOfService,
                    time: formData.timeOfService,
                    status: 'pending',
                    service_data:
                        formData?.servicesData?.map((service) => ({
                            ...service,
                            service: service.service || null, // Handle empty service selection
                            serviceName: service.serviceName || '', // Handle empty service name
                            price: parseFloat(cleanPrice(service.price)),
                        })) ?? [],
                    location_id: persistedLocationId,
                    appointment_type: isTelemedicine ? 'telemedicine' : 'in_office',
                    new_patient: newPatientData,
                    service_duration: duration,
                    created_at: new Date(),
                };

                const response = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/appointments`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify(createPayload),
                    },
                );

                const result = await response.json();

                if (!response.ok || result.error) {
                    const errorMessage = result.error || 'Failed to create appointment';
                    throw new Error(errorMessage);
                }

                // Backend may return { success, data } or the created row directly
                let data;
                if (result.success && result.data) {
                    data = Array.isArray(result.data) ? result.data : [result.data];
                } else if (Array.isArray(result)) {
                    data = result;
                } else {
                    data = result ? [result] : [];
                }

                if (data[0]?.id) {
                    const appointmentId = data[0]?.id;

                    if (formData?.name) {
                        sendEmailToNewPatient(
                            formData?.name,
                            patientEmail,
                            business?.name,
                            business?.email,
                            formData?.dob,
                            formData?.gender,
                            formData?.phone,
                            appointmentId,
                        );
                    } else {
                        // Handle both object and array cases for providerDetails
                        const providerDetail = providerDetails ? (Array.isArray(providerDetails) ? providerDetails[0] : providerDetails) : null;
                        const providerFirstName = isBusinessSelection
                            ? (business?.name || 'Business')
                            : (providerDetail ? providerDetail.first_name : 'Provider');
                        sendEmailToPatient(
                            patientEmail,
                            appointmentId,
                            selectedPatient?.first_name,
                            business?.name,
                            business?.email,
                            timeOfService,
                            dateOfService,
                            providerFirstName,
                            visitCost,
                            emailLocation,
                            serviceNames,
                        );
                    }
                }
                handlePopupToggle();
                getAppointmentList().then(() => {
                    setFormData({
                        form_status: 'add',
                    });
                });
                // Show "Appointment booked" message
                toasterNotification('success', 'Appointment booked');
            }
        } catch (err) {
            console.error('Error in handleSubmit:', err.message);
            console.error('Error stack:', err.stack);
            toasterNotification('error', '', 'An error occurred while processing the appointment.');
        }
    };

    const tabClass = 'business-tab-label';
    const tabClassActive = 'business-tab-label active';

    const getAppointmentList = useCallback(async (filters = {}) => {
        try {
            // Validate business_id
            if (!business?.id) {
                console.error('Business ID is required');
                setAppointments([]);
                return;
            }

            // Wait for auth to finish loading
            if (authLoading) {
                return;
            }
            // Validate access token
            if (!token) {
                console.error('No authentication token found');
                setAppointments([]);
                return;
            }

            // Build request body
            const requestBody = { business_id: business.id };
            if (filters.providerId) {
                requestBody.provider_id = filters.providerId;
            }

            // Fetch appointments from backend API
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/get-appointments`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(requestBody)
                }
            );

            const result = await response.json();

            if (!response.ok || result.error) {
                const errorMessage = result.error || 'Failed to fetch appointments';
                console.error('Error fetching appointments:', errorMessage);
                setAppointments([]);
                return;
            }

            const data = result.data || [];
            setAppointments(data);
        } catch (error) {
            console.error('Error fetching appointments:', error);
            setAppointments([]);
        }
    }, [token, business?.id, authLoading]);

    const getOooByProviderId = useCallback(async (providerId) => {
        if (!providerId) return [];

        // Use cached ooo from full details fetch when available
        const cached = providerFullDetails.find((item) => (item.provider ?? item).id === providerId);
        if (cached) {
            return Array.isArray(cached.ooo) ? cached.ooo : [];
        }

        if (!token) return [];
        try {
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/provider-ooo`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ provider_id: providerId }),
                }
            );
            if (!response.ok) return [];
            const result = await response.json();
            return result.success && result.data ? (Array.isArray(result.data) ? result.data : []) : [];
        } catch (error) {
            console.error('Error fetching provider OOO:', error);
            return [];
        }
    }, [token, providerFullDetails]);
    const getProviderAvailablity = useCallback(async (id) => {
        if (!id) return { providerAvailablity: [], providerDetails: [] };

        // Use cached data from full details fetch when available
        const cached = providerFullDetails.find((item) => (item.provider ?? item).id === id);
        if (cached) {
            return {
                providerAvailablity: Array.isArray(cached.availability) ? cached.availability : [],
                providerDetails: cached.provider ?? cached,
            };
        }

        if (!token) return { providerAvailablity: [], providerDetails: [] };
        try {
            const [availabilityResponse, providerResponse] = await Promise.all([
                fetch(`${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${id}/availability`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                }),
                fetch(`${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${id}`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                }),
            ]);

            let providerAvailablity = [];
            let providerDetails = [];

            if (availabilityResponse.ok) {
                const r = await availabilityResponse.json();
                providerAvailablity = Array.isArray(r.data) ? r.data : (Array.isArray(r) ? r : []);
            }
            if (providerResponse.ok) {
                const r = await providerResponse.json();
                const data = r.data ?? r;
                providerDetails = Array.isArray(data) ? data : data;
            }

            return { providerAvailablity, providerDetails };
        } catch (error) {
            console.error('Error fetching provider availability:', error);
            return { providerAvailablity: [], providerDetails: [] };
        }
    }, [token, providerFullDetails]);

    const processAppointment = useCallback(async (appointment, providersMap = null, patientsMap = null, servicesMap = null) => {
        try {
            // Validate access token
            if (!token) {
                console.error('No authentication token found');
                // Return appointment with minimal processing if no token
                return {
                    ...appointment,
                    provider: null,
                    patient: appointment?.new_patient ? [appointment.new_patient][0] : null,
                    services: [],
                    service_data: appointment?.service_data || [],
                    patient_name: appointment?.new_patient?.full_name || '',
                    scheduled: appointment?.appointment_date,
                    type: 'appointment',
                    provider_name: business?.name || 'Business',
                    status: appointment?.status,
                    location_id: appointment?.location_id,
                };
            }

            let provider = null;
            let providerFullName = '';
            
            // Use pre-fetched provider from map if available, otherwise skip (map is built by caller)
            if (appointment?.provider_id) {
                if (providersMap && providersMap.has(appointment.provider_id)) {
                    provider = providersMap.get(appointment.provider_id);
                    providerFullName = `${provider?.first_name || ''} ${provider?.last_name || ''}`.trim();
                } else if (!providersMap) {
                    // Fallback: fetch single provider only when no map provided (e.g. single appointment flow)
                    try {
                        const providerResponse = await fetch(
                            `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${appointment.provider_id}`,
                            {
                                method: 'GET',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                }
                            }
                        );

                        if (providerResponse.ok) {
                            const providerResult = await providerResponse.json();
                            if (providerResult.success && providerResult.data) {
                                const data = providerResult.data;
                                provider = Array.isArray(data) ? data[0] : data;
                                providerFullName = `${provider?.first_name || ''} ${provider?.last_name || ''}`.trim();
                            }
                        }
                    } catch (error) {
                        console.error('Error fetching provider:', error);
                    }
                }
            }

            let patient = null;
            let patientName = '';
            
            if (appointment?.patient_id) {
                if (patientsMap && patientsMap.has(appointment.patient_id)) {
                    const p = patientsMap.get(appointment.patient_id);
                    patient = [p];
                    patientName = p?.name || `${p?.first_name || ''} ${p?.last_name || ''}`.trim();
                } else if (!patientsMap) {
                    try {
                        const patientResponse = await fetch(
                            `${process.env.REACT_APP_SERVER_URL}/api/providers/patients`,
                            {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                },
                                body: JSON.stringify({ ids: [appointment.patient_id] })
                            }
                        );

                        if (patientResponse.ok) {
                            const patientResult = await patientResponse.json();
                            if (patientResult.success && patientResult.data !== undefined) {
                                const data = patientResult.data;
                                patient = Array.isArray(data) ? data : [data];
                                if (patient && patient[0]) {
                                    patientName = patient[0].name || `${patient[0].first_name || ''} ${patient[0].last_name || ''}`;
                                } else {
                                    patientName = '';
                                }
                            }
                        }
                    } catch (error) {
                        console.error('Error fetching patient:', error);
                    }
                }
            } else if (appointment?.new_patient) {
                // Use new_patient data if no linked patient
                patient = [appointment?.new_patient];
                patientName = appointment?.new_patient?.full_name || `${appointment?.new_patient?.first_name || ''} ${appointment?.new_patient?.last_name || ''}`;
            }

            // Ensure service_data exists and is properly formatted
            let serviceData = appointment?.service_data || [];
            let serviceIds = Array.isArray(serviceData) ? serviceData.map((item) => item && item.service).filter(Boolean) : [];
            let services = [];

            if (serviceIds.length > 0) {
                if (servicesMap) {
                    services = serviceIds
                        .map(id => servicesMap.get(Number(id)) || servicesMap.get(id))
                        .filter(Boolean);
                } else {
                    try {
                        const servicesResponse = await fetch(
                            `${process.env.REACT_APP_SERVER_URL}/api/providers/services`,
                            {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                },
                                body: JSON.stringify({ ids: serviceIds })
                            }
                        );

                        if (servicesResponse.ok) {
                            const servicesResult = await servicesResponse.json();
                            if (servicesResult.success && servicesResult.data !== undefined) {
                                services = Array.isArray(servicesResult.data) ? servicesResult.data : [];
                            } else if (Array.isArray(servicesResult)) {
                                services = servicesResult;
                            }
                        }
                    } catch (error) {
                        console.error('Error fetching services:', error);
                    }
                }
            }

            // Match service information with service data
            if (services && services.length > 0 && serviceData.length > 0) {
                // Ensure service details are complete in service_data
                serviceData = serviceData.map((item, idx) => {
                    if (!item) {
                        return {};
                    }
                    const serviceInfo = services.find(s => s.id === Number(item.service));
                    if (!item.serviceName && serviceInfo && serviceInfo.name) {
                    }
                    return {
                        ...item,
                        serviceName: item.serviceName || (serviceInfo ? serviceInfo.name : ''),
                        description: item.description || (serviceInfo ? serviceInfo.description : ''),
                        price: item.price !== undefined ? item.price : (serviceInfo ? serviceInfo.price : '')
                    };
                });
            }

            const computedProviderName = appointment?.provider_id ? providerFullName : (business?.name || 'Business');

            return {
                ...appointment,
                provider: provider ?? null,
                patient: patient?.[0] ?? null,
                services: services,
                service_data: serviceData,
                patient_name: patientName,
                scheduled: appointment?.appointment_date,
                type: 'appointment',
                provider_name: computedProviderName,
                status: appointment?.status,
                location_id: appointment?.location_id,
            };
        } catch (error) {
            console.error('Error processing appointment:', error);
            // Return appointment with minimal processing on error
            return {
                ...appointment,
                provider: null,
                patient: appointment?.new_patient ? [appointment.new_patient][0] : null,
                services: [],
                service_data: appointment?.service_data || [],
                patient_name: appointment?.new_patient?.full_name || '',
                scheduled: appointment?.appointment_date,
                type: 'appointment',
                provider_name: business?.name || 'Business',
                status: appointment?.status,
                location_id: appointment?.location_id,
            };
        }
    }, [token, business]);

    const next = () => {
        if (value === '1') {
            let date = selectedMonth;
            let nextMonth = new Date(date.setMonth(date.getMonth() + 1));
            let firstOfNextMonth = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1);
            setSelectedMonth(nextMonth);
            setMonthStartDate(firstOfNextMonth);
            setPageTabHeading(formatDateHeading(nextMonth, 'month'));
        }

        if (value === '2') {
            let date = weakStartDate;
            let nextWeek = new Date(date.setDate(date.getDate() + 7));
            let nextWeekEnd = new Date(moment(nextWeek).add(6, 'days'));

            setWeakStartDate(nextWeek);
            setWeakEndDate(nextWeekEnd);
            setPageTabHeading(formatDateHeading(nextWeek, 'week'));
        }

        if (value === '3') {
            let date = selectedDate;
            let nextDay = new Date(date.setDate(date.getDate() + 1));
            setSelectedDate(nextDay);
            setPageTabHeading(formatDateHeading(nextDay, 'day'));
        }
    };

    const prev = () => {
        if (value === '1') {
            let date = selectedMonth;
            let prevMonth = new Date(date.setMonth(date.getMonth() - 1));
            let firstOfPrevMonth = new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1);
            setSelectedMonth(prevMonth);
            setMonthStartDate(firstOfPrevMonth);
            setPageTabHeading(formatDateHeading(prevMonth, 'month'));
        }

        if (value === '2') {
            let date = weakStartDate;
            let prevWeek = new Date(date.setDate(date.getDate() - 7));
            let prevWeekEnd = new Date(moment(prevWeek).add(6, 'days'));

            setWeakStartDate(prevWeek);
            setWeakEndDate(prevWeekEnd);
            setPageTabHeading(formatDateHeading(prevWeek, 'week'));
        }

        if (value === '3') {
            let date = selectedDate;
            let prevDay = new Date(date.setDate(date.getDate() - 1));
            setSelectedDate(prevDay);
            setPageTabHeading(formatDateHeading(prevDay, 'day'));
        }
    };

    const today = () => {
        if (value === '1') {
            const date = new Date();
            setSelectedMonth(date);
            setMonthStartDate(new Date(date.getFullYear(), date.getMonth(), 1));
            setPageTabHeading(formatDateHeading(date, 'month'));
        }

        if (value === '2') {
            let currentDay = new Date().getDay();
            let dateOfDayZero = new Date(new Date().setDate(new Date().getDate() - currentDay + 1));

            setWeakStartDate(dateOfDayZero);
            setWeakEndDate(new Date(moment(dateOfDayZero).add(6, 'days')));
            setPageTabHeading(formatDateHeading(dateOfDayZero, 'week'));
        }

        if (value === '3') {
            const date = new Date();
            setSelectedDate(date);
            setPageTabHeading(formatDateHeading(date, 'day'));
        }
    };

    const getBusinessMainLocation = useCallback(async () => {
        if (business?.id) {
            try {
                if (!token) {
                    console.error('No authentication token found');
                    return 'No Location Available';
                }
                const data = await getLocationForBusinessDataApi(business?.id, token);
                
                // Preserve object structure - handle both array and object cases
                const locationData = Array.isArray(data) ? (data.length > 0 ? data[0] : null) : data;
                
                if (locationData) {
                    const location =
                        (locationData.name || '') +
                        ' ' +
                        (locationData.address || '') +
                        ' , ' +
                        (locationData.city || '') +
                        ' , ' +
                        (locationData.state || '') +
                        ' ' +
                        (locationData.suite || '') +
                        ' , ' +
                        (locationData.zip || '');
                    return location;
                } else {
                    return 'No Location Available';
                }
            } catch (error) {
                console.error('Error getting business location:', error);
                return 'No Location Available';
            }
        }
        return 'No Location Available';
    }, [token, business?.id]);

    useEffect(() => {
        if (!authLoading && token) {
            handleTabChange(null, '3');
            getAppointmentList().then(() => {});
            getProviders().then(() => {});
        }
    }, [authLoading, token, getAppointmentList, getProviders]);

    useEffect(() => {
        if (appointments?.length > 0) {
            const processAllAppointments = async () => {
                // Collect unique provider IDs to fetch each provider only once
                const uniqueProviderIds = [...new Set(
                    appointments
                        .map(a => a?.provider_id)
                        .filter(Boolean)
                )];
                // Collect unique patient IDs to fetch each patient only once
                const uniquePatientIds = [...new Set(
                    appointments
                        .map(a => a?.patient_id)
                        .filter(Boolean)
                )];
                // Collect unique service IDs from all appointments' service_data
                const uniqueServiceIds = [...new Set(
                    appointments.flatMap(a =>
                        (Array.isArray(a?.service_data) ? a.service_data : [])
                            .map(item => item?.service)
                            .filter(Boolean)
                    )
                )];

                // Build from already-fetched providers via ref — no extra API call needed
                const providersMap = new Map(
                    providersRef.current.filter((p) => p && p.id != null).map((p) => [p.id, p])
                );

                let patientsMap = new Map();
                if (uniquePatientIds.length > 0 && token) {
                    try {
                        const patientResponse = await fetch(
                            `${process.env.REACT_APP_SERVER_URL}/api/providers/patients`,
                            {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                },
                                body: JSON.stringify({ ids: uniquePatientIds })
                            }
                        );
                        if (patientResponse.ok) {
                            const patientResult = await patientResponse.json();
                            if (patientResult.success && patientResult.data !== undefined) {
                                const data = patientResult.data;
                                const list = Array.isArray(data) ? data : (data ? [data] : []);
                                list.forEach(p => {
                                    if (p && p.id != null) patientsMap.set(p.id, p);
                                });
                            }
                        }
                    } catch (error) {
                        console.error('Error fetching patients for appointments:', error);
                    }
                }

                let servicesMap = new Map();
                if (uniqueServiceIds.length > 0 && token) {
                    try {
                        const servicesResponse = await fetch(
                            `${process.env.REACT_APP_SERVER_URL}/api/providers/services`,
                            {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                },
                                body: JSON.stringify({ ids: uniqueServiceIds })
                            }
                        );
                        if (servicesResponse.ok) {
                            const servicesResult = await servicesResponse.json();
                            if (servicesResult.success && servicesResult.data !== undefined) {
                                const data = servicesResult.data;
                                const list = Array.isArray(data) ? data : (data ? [data] : []);
                                list.forEach(s => {
                                    if (s && s.id != null) servicesMap.set(s.id, s);
                                });
                            }
                        }
                    } catch (error) {
                        console.error('Error fetching services for appointments:', error);
                    }
                }

                // Process all appointments in parallel using the shared provider, patient, and service maps
                const processedResults = await Promise.all(
                    appointments.map(appointment => processAppointment(appointment, providersMap, patientsMap, servicesMap))
                );
                // Set processed appointments all at once
                setProcessedAppointments(processedResults);
            };
            
            processAllAppointments();
        } else {
            setProcessedAppointments([]);
        }
    }, [appointments, processAppointment, token, providers]);
    return (
        <ThemeProvider theme={materialTheme}>
            <TabContext value={value}>
                <PageStructure 
                    heading="Appointments" 
                    hideHeaderBorder={true}
                    tabs={
                        <Box 
                            data-tabs="true"
                            sx={{ 
                                borderBottom: 1, 
                                borderColor: 'divider', 
                                backgroundColor: 'white !important',
                                width: '100%',
                                margin: 0,
                                marginLeft: 0,
                                marginRight: 0,
                                paddingLeft: isMobile ? '20px' : (isTabletOrBelow ? '32px' : '60px'),
                                paddingRight: shouldScroll ? '16px' : (isMobile ? '20px' : (isTabletOrBelow ? '32px' : '60px')),
                                boxSizing: 'border-box',
                            }}
                        >
                            <Tabs 
                                value={value} 
                                onChange={handleTabChange}
                                indicatorColor={'false'} 
                                variant={shouldScroll ? 'scrollable' : 'standard'}
                                scrollButtons={shouldScroll ? 'auto' : false}
                                allowScrollButtonsMobile
                                sx={{ 
                                    backgroundColor: 'white !important',
                                    marginLeft: 0,
                                    marginRight: 0,
                                    width: '100%',
                                    '& .MuiTabs-scrollButtons': {
                                        width: '48px !important',
                                        flexShrink: 0,
                                        minWidth: '48px',
                                        marginLeft: shouldScroll ? '12px' : 0,
                                    },
                                    '& .MuiTabs-scrollButtons:first-of-type': {
                                        marginLeft: shouldScroll ? '12px' : 0,
                                        marginRight: shouldScroll ? 0 : undefined,
                                    },
                                    '& .MuiTabs-scrollButtons:last-of-type': {
                                        marginLeft: shouldScroll ? 0 : undefined,
                                        marginRight: shouldScroll ? '16px' : 0,
                                    },
                                    '& .MuiTabs-scrollButtons .MuiSvgIcon-root': {
                                        fontSize: '24px !important',
                                        width: '24px',
                                        height: '24px',
                                    },
                                }} 
                            >
                                <Tab
                                    label="Day"
                                    value="3"
                                    className={value === '3' ? tabClassActive : tabClass}
                                    sx={{
                                        textTransform: 'none',
                                        minWidth: shouldScroll ? 'auto' : undefined,
                                        padding: shouldScroll ? { xs: '12px 16px', sm: '12px 24px' } : undefined,
                                    }}
                                />
                                <Tab
                                    label="Week"
                                    value="2"
                                    className={value === '2' ? tabClassActive : tabClass}
                                    sx={{
                                        textTransform: 'none',
                                        minWidth: shouldScroll ? 'auto' : undefined,
                                        padding: shouldScroll ? { xs: '12px 16px', sm: '12px 24px' } : undefined,
                                    }}
                                />
                                <Tab
                                    label="Month"
                                    value="1"
                                    className={value === '1' ? tabClassActive : tabClass}
                                    sx={{
                                        textTransform: 'none',
                                        minWidth: shouldScroll ? 'auto' : undefined,
                                        padding: shouldScroll ? { xs: '12px 16px', sm: '12px 24px' } : undefined,
                                    }}
                                />
                            </Tabs>
                        </Box>
                    }
                >
                    <Box
                        sx={{
                            backgroundColor: theme.colorBackground,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Box sx={{ 
                            paddingTop: '48px', // Match business page: pt:1 (8px) + InnerPageLayout top padding (40px) = 48px
                            paddingBottom: '40px',
                            paddingLeft: '60px',
                            paddingRight: '60px',
                            '@media (max-width: 960px)': {
                                paddingLeft: '32px',
                                paddingRight: '32px',
                            },
                            '@media (max-width: 640px)': {
                                paddingLeft: '20px',
                                paddingRight: '20px',
                            },
                        }}>
                            {/* Responsive layout: Changes when content would overflow, not just tablet breakpoint */}
                            <Box sx={{
                                display: 'flex',
                                flexDirection: isCompactHeader ? 'column' : 'row',
                                alignItems: isCompactHeader ? 'flex-start' : 'center',
                                justifyContent: isCompactHeader ? 'flex-start' : 'space-between',
                                gap: isCompactHeader ? '16px' : 0
                            }}>
                                {/* Navigation - left side on desktop, below date when compact */}
                                <Box sx={{
                                    order: isCompactHeader ? 2 : 1,
                                    width: isCompactHeader ? '100%' : 'auto',
                                    display: isCompactHeader ? 'flex' : 'block',
                                    justifyContent: isCompactHeader ? 'space-between' : 'flex-start',
                                    alignItems: 'center'
                                }}>
                                <Navigation next={next} prev={prev} today={today} />
                                    {/* Actions on compact layout - shown inline with navigation */}
                                    <Box sx={{ display: isCompactHeader ? 'flex' : 'none', gap: '8px' }}>
                                        <FilterButton getAppointmentList={getAppointmentList} providers={providers} />
                                        <Button onClick={handlePopupToggle} disabled={loading}>
                                            {loading ? 'Fetching Data...' : 'Create Appointment'}
                                        </Button>
                                    </Box>
                                </Box>
                                
                                {/* Date header - centered on desktop, top when compact */}
                                <Box sx={{
                                    order: isCompactHeader ? 1 : 2,
                                    width: isCompactHeader ? '100%' : 'auto',
                                    textAlign: isCompactHeader ? 'left' : 'center',
                                    flex: isCompactHeader ? 'none' : 1,
                                    marginBottom: isCompactHeader ? '8px' : 0,
                                    marginLeft: isCompactHeader ? 0 : '32px'
                                }}>
                                    <SubHeading sx={{ whiteSpace: 'nowrap' }}>{pageTabHeading}</SubHeading>
                                </Box>
                                
                                {/* Actions - right side on desktop, hidden when compact */}
                                <Box sx={{
                                    order: 3,
                                    width: isCompactHeader ? '100%' : 'auto',
                                    display: isCompactHeader ? 'none' : 'flex',
                                    justifyContent: 'flex-end'
                                }}>
                                <div className={'flex space-x-2'}>
                                    <FilterButton getAppointmentList={getAppointmentList} providers={providers} />
                                    <Button onClick={handlePopupToggle} disabled={loading}>
                                        {loading ? 'Fetching Data...' : 'Create Appointment'}
                                    </Button>
                                </div>
                                </Box>
                            </Box>

                            <TabPanel value="1" className={'p-lr'}>
                                <MonthView
                                    startDate={monthStartDate}
                                    appointments={processedAppointments}
                                    handlePopupToggle={handleEditAppointment}
                                />
                            </TabPanel>

                            <TabPanel value="2" className={'p-lr'}>
                                <WeakView
                                    startDate={weakStartDate}
                                    appointments={processedAppointments}
                                    handlePopupToggle={handleEditAppointment}
                                />
                            </TabPanel>

                            <TabPanel value="3" className={'p-lr'}>
                                <DayView
                                    date={selectedDate}
                                    rows={processedAppointments}
                                    togglePopup={handleEditAppointment}
                                />
                            </TabPanel>
                        </Box>
                    </Box>
                </PageStructure>

                <AppointmentPopup
                    open={openPopup}
                    onClose={handlePopupToggle}
                    handleSubmit={handleSubmit}
                    formData={formData}
                    setFormData={setFormData}
                    getAppointmentList={getAppointmentList}
                    providers={providers}
                    setProviders={setProviders}
                    providerFullDetails={providerFullDetails}
                />
            </TabContext>
        </ThemeProvider>
    );
};

export default Appointment;